import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { fetchSuperCategories } from "../../../../Redux/filters/slice";
import { RootState } from "../../../../store";
import BrowsingHistoryMenu from "./BrowsingHistoryMenu";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_ECOMMERCE_PERFIX}`;

interface ParentCategory {
  _id: string;
  name: string;
  image?: string;
}

interface SubCategoryItem {
  _id: string;
  name: string;
}

interface CategoryMenuData {
  parents: ParentCategory[];
  subs: Record<string, SubCategoryItem[]>;
}

interface CategoriesBarProps {
  activeSuperCategory?: string;
}

const CategoriesBar = ({ activeSuperCategory }: CategoriesBarProps = {}) => {
  const dispatch = useAppDispatch();
  const { superCategories } = useAppSelector((state: RootState) => state.filters);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [menuCache, setMenuCache] = useState<Record<string, CategoryMenuData>>({});
  const [loadingMenu, setLoadingMenu] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (superCategories.data.length === 0 && !superCategories.loading) {
      dispatch(fetchSuperCategories({ categoryTypeName: "Ecommerce", showOnWebsite: true, showOnHomepage: true }));
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setActiveCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategoryClick = async (categoryName: string) => {
    if (activeCategory === categoryName) {
      setActiveCategory(null);
      return;
    }
    setActiveCategory(categoryName);

    if (categoryName === "Browsing History") {
      return;
    }

    if (menuCache[categoryName]) return;

    setLoadingMenu(true);
    try {
      const parentsRes = await axios.get(`${PRODUCT_BASE_URL}/categories/parent-categories`, {
        params: { superCategory: categoryName },
      });
      const parents: ParentCategory[] = parentsRes.data.data || [];

      // Fetch all sub-categories simultaneously
      const subResults = await Promise.all(
        parents.map((p) =>
          axios
            .get(`${PRODUCT_BASE_URL}/categories/sub-categories`, {
              params: { parentCategory: p.name },
            })
            .catch(() => ({ data: { data: [] } }))
        )
      );

      const subs: Record<string, SubCategoryItem[]> = {};
      parents.forEach((p, i) => {
        subs[p.name] = subResults[i].data.data || [];
      });

      setMenuCache((prev) => ({ ...prev, [categoryName]: { parents, subs } }));
    } catch (err) {
      console.error("Failed to load category menu:", err);
      setMenuCache((prev) => ({ ...prev, [categoryName]: { parents: [], subs: {} } }));
    }
    setLoadingMenu(false);
  };

  const categories = [
    ...(superCategories.data && superCategories.data.length > 0
      ? superCategories.data.slice(0, 7).map((cat) => ({ label: cat.name ?? "", id: cat._id }))
      : [
          { label: "Men", id: "men" },
          { label: "Women", id: "women" },
          { label: "Fresh", id: "fresh" },
          { label: "Groceries", id: "groceries" },
          { label: "Kids & Toys", id: "kids" },
          { label: "Beauty & Personal", id: "beauty" },
          { label: "Mobiles", id: "mobiles" },
          { label: "Electronics", id: "electronics" },
          { label: "Home & Kitchen Appliances", id: "home" },
          { label: "Furniture", id: "furniture" },
        ]),
    { label: "Browsing History", id: "browsing" },
    ...(superCategories.data && superCategories.data.length > 0 ? [] : [{ label: "Shopping Vouchers", id: "vouchers" }]),
  ];

  const activeData = activeCategory ? menuCache[activeCategory] : null;
  const showDropdown = !!activeCategory;
  const showLoading = loadingMenu && !activeData;

  return (
    <>
      <div ref={wrapperRef} style={{ position: "relative" }}>
        {/* Category bar */}
        <div className="container-fluid mt-n1" style={{ paddingLeft: "5px", paddingRight: "5px" }}>
          <div className="bg-gray-100 overflow-x-auto hide-scrollbar">
            <nav className="navbar navbar-expand-lg p-1 mt-0">
              <ul className="navbar-nav gap-2 text-nowrap flex-nowrap">
                  <li className="nav-item">
                  <a href="/DealsPage" className="text-decoration-none">
                    <b className="nav-link text-sm fw-normal text-black letter-spacing-1 cursor-pointer">
                     Deals
                    </b>
                  </a>
                </li>
                {superCategories.loading
                  ? Array.from({ length: 10 }).map((_, i) => (
                      <li className="nav-item opacity-50" key={i}>
                        <span className="nav-link text-sm fw-normal text-black skeleton-loader">Loading...</span>
                      </li>
                    ))
                  : categories.map(({ label, id }) => {
                      const isActive = activeSuperCategory?.toUpperCase() === label.toUpperCase();
                      const isOpen = activeCategory === label;
                      return (
                        <li className="nav-item" key={id}>
                          <b
                            className={`nav-link text-sm fw-normal letter-spacing-1 cursor-pointer categories-bar-item${
                              isActive ? " categories-bar-active" : " text-black"
                            }${isOpen ? " categories-bar-open" : ""}`}
                            onClick={() => handleCategoryClick(label)}
                            style={{ userSelect: "none" }}
                          >
                            {label}
                            <i className={`ri-arrow-drop-${isOpen ? "up" : "down"}-line ms-n1`}></i>
                          </b>
                        </li>
                      );
                    })}
                <li className="nav-item">
                  <a href="/BookAppointment" className="text-decoration-none">
                    <b className="nav-link text-sm fw-normal text-black letter-spacing-1 cursor-pointer">
                      Book an Appointment
                    </b>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        {/* Mega menu dropdown */}
        {showDropdown && (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              zIndex: 1050,
              background: "#fff",
              boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
              borderTop: "2px solid #f0f0f0",
              borderRadius: "0 0 10px 10px",
            }}
          >
            {activeCategory === "Browsing History" ? (
              <div style={{ padding: "28px 36px" }}>
                <BrowsingHistoryMenu />
              </div>
            ) : showLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                <p className="text-muted text-sm mt-2 mb-0">Loading categories…</p>
              </div>
            ) : !activeData || activeData.parents.length === 0 ? (
              <div className="text-center py-4 text-muted" style={{ fontSize: "13px" }}>
                No categories available
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                  gap: "0",
                  padding: "28px 36px",
                  maxHeight: "520px",
                  overflowY: "auto",
                }}
              >
                {activeData.parents.map((parent) => {
                  const subList = activeData.subs[parent.name] || [];
                  return (
                    <div key={parent._id} style={{ paddingRight: "24px", paddingBottom: "20px" }}>
                      <a
                        href={`/Categories/${activeCategory}?category=${encodeURIComponent(parent.name)}`}
                        className="text-decoration-none"
                        onClick={() => setActiveCategory(null)}
                      >
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#0f1111",
                            display: "block",
                            marginBottom: "10px",
                            lineHeight: 1.3,
                          }}
                        >
                          {parent.name}
                        </span>
                      </a>
                      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                        {subList.map((sub) => (
                          <li key={sub._id} style={{ marginBottom: "5px" }}>
                            <a
                              href={`/Categories/${activeCategory}?category=${encodeURIComponent(parent.name)}&subCategory=${encodeURIComponent(sub.name)}`}
                              className="mega-menu-sub-link"
                              onClick={() => setActiveCategory(null)}
                            >
                              {sub.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        .skeleton-loader {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
          border-radius: 4px;
          color: transparent !important;
          min-width: 80px;
          display: inline-block;
        }
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .categories-bar-item {
          position: relative;
          padding-bottom: 6px !important;
          color: #333;
        }
        .categories-bar-item::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 2px;
          background-color: #0dcaf0;
          transition: width 0.2s ease;
        }
      
        
        
       

        .mega-menu-sub-link {
          font-size: 13px;
          color: #555;
          text-decoration: none;
          line-height: 1.5;
          display: block;
        }
       
      `}</style>
    </>
  );
};

export default CategoriesBar;
